import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BcmApiService } from './bcm-api.service';
import { parseHttpResponseList, parseHttpResponseItem, parseHttpResponsePage } from '@shared/functions/parse-http-response';
import { buildDynamicPriceRuleParams } from '@shared/functions/build-dynamic-price-rule-params';
import { IProduct, Product } from '@shared/models/product';
import { Page } from '@shared/models/pagination';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';

@Injectable({
    providedIn: 'root'
})
export class ProductsApiService extends BcmApiService {

    private RESOURCE_ENDPOINT = 'products';

    // Evaluate a product's dynamic price rules against a booking-context (boat /
    // berth / tenantRelation / season). Each entity is sent as `<key>Id`; a manual
    // boat passes raw dimensions instead. Mirrors the legacy ProductService call so
    // the backend rule engine resolves the same `rulePrice`. The booking-v2 wizard
    // uses it to price relation Leistungen that carry rule sets (e.g. seasonal
    // "pro Meter" berth fees).
    evaluatePriceRule(
        productId: number,
        parameters: Record<string, { id?: number } | null | undefined>,
        quantity: number,
        boatDimensions?: { width: number; length: number },
        durations?: { days?: number; overnights?: number; travelers?: number },
    ): Observable<Partial<BcmDynamicPriceDto>> {
        let params = new HttpParams();
        for (const [key, value] of Object.entries(buildDynamicPriceRuleParams(parameters, quantity, boatDimensions, durations))) {
            params = params.append(key, value);
        }
        return super.get<Partial<BcmDynamicPriceDto>>(
            `${this.RESOURCE_ENDPOINT}/${productId}/dynamic-price/evaluate-price-rules`, params,
        );
    }

    /** Slim flat list for dropdowns, filters and autocomplete widgets. */
    getAll(params: HttpParams = new HttpParams()): Observable<Product[]> {
        return this.select(params);
    }

    select(params: HttpParams = new HttpParams()): Observable<Product[]> {
        return super.get<IProduct[]>(`${this.RESOURCE_ENDPOINT}/select`, params)
            .pipe(parseHttpResponseList<IProduct, Product>(Product));
    }

    index(params: HttpParams = new HttpParams()): Observable<Page<Product>> {
        return super.get<Page<IProduct>>(`${this.RESOURCE_ENDPOINT}/index`, params)
            .pipe(parseHttpResponsePage<IProduct, Product>(Product));
    }

    count(params: HttpParams = new HttpParams()): Observable<number> {
        return super.get<number>(this.RESOURCE_ENDPOINT + '/count', params);
    }

    getOne(id: number, params: HttpParams = new HttpParams()): Observable<Product> {
        return super.get<IProduct>(`${this.RESOURCE_ENDPOINT}/${id}`, params)
            .pipe(parseHttpResponseItem<IProduct, Product>(Product));
    }

    add(body: object = {}): Observable<number> {
        return super.post(this.RESOURCE_ENDPOINT, body);
    }

    update(id: number, body: object = {}): Observable<number> {
        return super.put(`${this.RESOURCE_ENDPOINT}/${id}`, body);
    }

    remove(id: number): Observable<number> {
        return super.delete(`${this.RESOURCE_ENDPOINT}/${id}`);
    }
}
